/**
 * Admin Drill Library
 *
 * CRUD page for stock drills in the `drills` collection.
 * Stock drills (isStock: true, coachId: null) are platform-wide and available
 * to all coaches via the DrillBrowser in the lesson editor.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ref, uploadBytesResumable, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { getStockDrills, createDrill, updateDrill, deleteDrill, preprocessDrillVideoForWeb } from '../../services/drills';
import type { Drill, DrillShotType } from '../../types/lesson';
import { useToast } from '../../hooks/useToast';
import { generateThumbnailFromFile } from '../../utils/videoThumbnail';

// ── Shot type config ──────────────────────────────────────────────────────────

const SHOT_TYPES: DrillShotType[] = ['forehand', 'backhand', 'serve', 'volley', 'return', 'movement', 'general'];

const SHOT_TYPE_COLORS: Record<DrillShotType, string> = {
  forehand:  'bg-blue-100 text-blue-700',
  backhand:  'bg-purple-100 text-purple-700',
  serve:     'bg-orange-100 text-orange-700',
  volley:    'bg-green-100 text-green-700',
  return:    'bg-cyan-100 text-cyan-700',
  movement:  'bg-amber-100 text-amber-700',
  general:   'bg-gray-100 text-gray-600',
};

// ── Stock drill catalog (mirrors pwa/scripts/seedStockDrills.js) ──────────────

interface CatalogDrill { title: string; description: string; shotType: DrillShotType }

const STOCK_CATALOG: CatalogDrill[] = [
  { title: 'Forehand Topspin Rally',          description: 'Stand at the baseline. Hit 20 consecutive forehands cross-court with heavy topspin. Focus on full follow-through over the shoulder.',                                                    shotType: 'forehand'  },
  { title: 'Forehand Inside-Out',             description: 'Run around your backhand corner and hit inside-out forehands to the ad court. 3 sets of 10. Focus on explosive hip rotation.',                                                          shotType: 'forehand'  },
  { title: 'Forehand Drop Shot',              description: 'From mid-court, practice forehand drop shots with underspin. Focus on soft hands and disguise — same backswing as a drive.',                                                            shotType: 'forehand'  },
  { title: 'One-Handed Backhand Drive',       description: 'Hit 20 one-handed backhands down the line. Focus on the unit turn and keeping the elbow away from the body through contact.',                                                           shotType: 'backhand'  },
  { title: 'Two-Handed Backhand Crosscourt',  description: 'Rally cross-court with two-handed backhands. Focus on weight transfer forward and keeping the non-dominant hand active through the shot.',                                              shotType: 'backhand'  },
  { title: 'Backhand Slice Approach',         description: 'Hit deep backhand slices and follow to net. 10 reps. Focus on staying low through the shot and getting the racquet under the ball.',                                                    shotType: 'backhand'  },
  { title: 'Flat Serve Down the T',           description: 'Practice 20 flat first serves aimed at the T. Toss slightly in front and to the right (for righties). Focus on full extension at contact.',                                            shotType: 'serve'     },
  { title: 'Kick Serve to Ad Court',          description: 'Toss behind your head and brush up on the ball. 20 reps to the ad court. Focus on pronation and getting heavy spin that kicks up off the bounce.',                                     shotType: 'serve'     },
  { title: 'Split-Step Volley Drill',         description: 'Partner feeds alternating forehand/backhand volleys. Split-step between each. 3 sets of 10. Focus on short backswing and firm wrist.',                                                  shotType: 'volley'    },
  { title: 'Reflex Volley at Net',            description: 'Stand at the net strap. Partner hits hard from the service line. React and block volleys deep. 2 sets of 15.',                                                                         shotType: 'volley'    },
  { title: 'Return of Serve — Neutral Rally', description: 'Focus on getting the return deep and crosscourt. No winners — just consistent depth. 20 returns, track how many land past the service line.',                                          shotType: 'return'    },
  { title: 'Chip & Charge Return',            description: 'Slice the return low and follow to net. Practice reading the serve toss to anticipate direction. 10 reps each side.',                                                                   shotType: 'return'    },
  { title: 'Shadow Footwork — Diamond Drill', description: 'No ball. Sprint to each corner of the court in a diamond pattern: deuce corner → net → ad corner → baseline center. 5 sets. Focus on split-steps at each point.',                     shotType: 'movement'  },
  { title: 'Recovery Steps Drill',            description: 'After each groundstroke, practice explosive recovery steps back to center. Use shuffle steps, not crossover. 3 sets of 10 shots.',                                                      shotType: 'movement'  },
  { title: 'Ball Machine Consistency',        description: 'Set ball machine to moderate pace, alternating sides. Hit 50 balls in a row without missing. Track your personal best streak.',                                                         shotType: 'general'   },
  { title: 'Wall Rally Warmup',               description: 'Hit against a wall for 5 minutes. Alternate between forehands and backhands every 10 shots. Stay light on your feet and maintain a rally rhythm.',                                      shotType: 'general'   },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function toSlug(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function isPlaceholder(url: string) {
  return !url || url.startsWith('https://placeholder.drill/');
}

async function uploadStockVideo(
  drillId: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<{ url: string; path: string }> {
  const ext = file.name.split('.').pop() ?? 'mp4';
  // Versioned path — never overwrites old files so existing lesson snapshots remain valid
  const path = `drills/stock/${drillId}/${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, path });
      },
    );
  });
}

async function uploadStockThumbnail(drillId: string, blob: Blob): Promise<string> {
  const path = `drills/stock/${drillId}/thumbnail.jpg`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
  return getDownloadURL(storageRef);
}

// ── Add / Edit Modal ──────────────────────────────────────────────────────────

interface DrillModalProps {
  drill?: Drill;
  onClose: () => void;
  onSaved: (drill: Drill, videoWasUploaded: boolean) => void;
}

function DrillModal({ drill, onClose, onSaved }: DrillModalProps) {
  const { showError } = useToast();
  const [title, setTitle] = useState(drill?.title ?? '');
  const [description, setDescription] = useState(drill?.description ?? '');
  const [shotType, setShotType] = useState<DrillShotType>(drill?.shotType ?? 'general');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Thumbnail state: explicit image upload takes priority over auto-generated frame
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [autoThumbBlob, setAutoThumbBlob] = useState<Blob | null>(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState<string | null>(drill?.thumbnailUrl ?? null);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!drill;
  const hasRealVideo = drill && !isPlaceholder(drill.videoUrl);

  // Auto-generate a thumbnail preview whenever a new video file is selected
  useEffect(() => {
    if (!videoFile) return;
    let objectUrl: string | null = null;
    generateThumbnailFromFile(videoFile, 1).then((blob) => {
      setAutoThumbBlob(blob);
      // Only show auto-gen preview if admin hasn't picked an explicit image
      if (!thumbnailFile) {
        objectUrl = URL.createObjectURL(blob);
        setThumbPreviewUrl(objectUrl);
      }
    }).catch(() => { /* ignore — VideoFrameThumb handles display fallback */ });
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFile]);

  // Update preview when admin picks an explicit thumbnail image
  function handleThumbnailFileChange(file: File | null) {
    setThumbnailFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setThumbPreviewUrl(url);
    } else {
      // Revert to auto-gen preview or existing URL
      if (autoThumbBlob) {
        setThumbPreviewUrl(URL.createObjectURL(autoThumbBlob));
      } else {
        setThumbPreviewUrl(drill?.thumbnailUrl ?? null);
      }
    }
  }

  const handleSave = useCallback(async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (isEditing) {
        let videoUrl = drill.videoUrl;
        let videoStoragePath = drill.videoStoragePath;
        if (videoFile) {
          const result = await uploadStockVideo(drill.id, videoFile, setUploadProgress);
          videoUrl = result.url;
          videoStoragePath = result.path;
        }
        // Upload thumbnail: explicit file → auto-gen blob → keep existing
        let thumbnailUrl = drill.thumbnailUrl;
        const thumbSource = thumbnailFile ?? autoThumbBlob;
        if (thumbSource) {
          thumbnailUrl = await uploadStockThumbnail(drill.id, thumbSource);
        }
        const updates: Parameters<typeof updateDrill>[1] = { title: title.trim(), description: description.trim(), shotType, videoUrl, videoStoragePath };
        if (thumbnailUrl !== undefined) updates.thumbnailUrl = thumbnailUrl;
        await updateDrill(drill.id, updates);
        onSaved({ ...drill, ...updates, thumbnailUrl }, !!videoFile);
      } else {
        const slug = toSlug(title.trim());
        const drillData: Omit<Drill, 'id' | 'createdAt'> = {
          title: title.trim(),
          description: description.trim(),
          shotType,
          videoUrl: `https://placeholder.drill/${slug}.mp4`,
          videoStoragePath: `drills/stock/${slug}.mp4`,
          isStock: true,
          coachId: null,
        };
        const id = await createDrill(drillData);
        if (videoFile) {
          const result = await uploadStockVideo(id, videoFile, setUploadProgress);
          await updateDrill(id, { videoUrl: result.url, videoStoragePath: result.path });
          drillData.videoUrl = result.url;
          drillData.videoStoragePath = result.path;
        }
        const thumbSource = thumbnailFile ?? autoThumbBlob;
        if (thumbSource) {
          drillData.thumbnailUrl = await uploadStockThumbnail(id, thumbSource);
          await updateDrill(id, { thumbnailUrl: drillData.thumbnailUrl });
        }
        onSaved({ id, ...drillData, createdAt: new Date().toISOString() } as Drill, !!videoFile);
      }
    } catch (err) {
      showError('Failed to save drill');
      console.error(err);
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  }, [title, description, shotType, videoFile, thumbnailFile, autoThumbBlob, drill, isEditing, onSaved, showError]);

  return (
    <div data-testid="drill-modal" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{isEditing ? 'Edit Drill' : 'Add Drill'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-400">*</span></label>
            <input
              data-testid="drill-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. Forehand Topspin Rally"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              data-testid="drill-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
              placeholder="Setup, cues, and rep counts"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Shot Type</label>
            <div className="flex flex-wrap gap-2">
              {SHOT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`shot-type-chip-${t}`}
                  onClick={() => setShotType(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    shotType === t ? 'border-blue-500 bg-blue-50 text-blue-700' : `border-gray-200 hover:border-gray-400 ${SHOT_TYPE_COLORS[t]}`
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Video {isEditing ? (hasRealVideo ? '(replace existing)' : '(upload to replace placeholder)') : '(optional — can upload later)'}
            </label>
            {videoFile ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                <span className="text-sm text-gray-700 truncate">{videoFile.name}</span>
                <button onClick={() => { setVideoFile(null); setAutoThumbBlob(null); if (!thumbnailFile) setThumbPreviewUrl(drill?.thumbnailUrl ?? null); }} className="ml-auto text-gray-400 hover:text-red-500 text-xs flex-shrink-0">Remove</button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg py-5 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm"
              >
                Click to select video file
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)} />
          </div>

          {/* Thumbnail */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Thumbnail <span className="text-gray-400 font-normal">(auto-generated from video if not set)</span>
            </label>
            <div className="flex items-start gap-3">
              {/* Preview */}
              <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                {thumbPreviewUrl ? (
                  <img src={thumbPreviewUrl} alt="Thumbnail preview" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24"><polygon points="6 4 20 12 6 20 6 4" /></svg>
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <button
                  type="button"
                  onClick={() => thumbInputRef.current?.click()}
                  className="w-full border border-gray-300 rounded-lg py-2 text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors text-sm"
                >
                  {thumbPreviewUrl ? 'Replace thumbnail' : 'Upload thumbnail image'}
                </button>
                {thumbnailFile && (
                  <button
                    type="button"
                    onClick={() => handleThumbnailFileChange(null)}
                    className="text-xs text-gray-400 hover:text-red-500"
                  >
                    Remove custom thumbnail
                  </button>
                )}
                <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleThumbnailFileChange(e.target.files?.[0] ?? null)} />
              </div>
            </div>
          </div>

          {uploadProgress !== null && (
            <div className="space-y-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-xs text-gray-500">Uploading… {uploadProgress}%</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50">Cancel</button>
          <button
            data-testid="save-drill-button"
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {saving ? (uploadProgress !== null ? 'Uploading…' : 'Saving…') : (isEditing ? 'Save changes' : 'Add drill')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Seed modal ────────────────────────────────────────────────────────────────

interface SeedResult { seeded: string[]; skipped: number }

function SeedModal({ existing, onClose, onDone }: { existing: Drill[]; onClose: () => void; onDone: (newDrills: Drill[]) => void }) {
  const existingTitles = new Set(existing.map((d) => d.title));
  const toSeed = STOCK_CATALOG.filter((c) => !existingTitles.has(c.title));

  const [seeding, setSeeding] = useState(false);
  const [result, setResult] = useState<SeedResult | null>(null);
  const { showError } = useToast();

  const handleSeed = useCallback(async () => {
    setSeeding(true);
    const seeded: string[] = [];
    const newDrills: Drill[] = [];
    let errors = 0;
    for (const catalog of toSeed) {
      try {
        const slug = toSlug(catalog.title);
        const drillData = {
          title: catalog.title,
          description: catalog.description,
          shotType: catalog.shotType,
          videoUrl: `https://placeholder.drill/${slug}.mp4`,
          videoStoragePath: `drills/stock/${slug}.mp4`,
          isStock: true as const,
          coachId: null,
        };
        const id = await createDrill(drillData);
        seeded.push(catalog.title);
        newDrills.push({ id, ...drillData, createdAt: new Date().toISOString() } as Drill);
      } catch {
        errors++;
      }
    }
    if (errors) showError(`${errors} drill(s) failed to seed`);
    setResult({ seeded, skipped: STOCK_CATALOG.length - toSeed.length });
    setSeeding(false);
    onDone(newDrills);
  }, [toSeed, onDone, showError]);

  return (
    <div data-testid="seed-modal" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Seed Stock Drills</h2>
          <button onClick={onClose} disabled={seeding} className="text-gray-400 hover:text-gray-700 disabled:opacity-50">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 py-4">
          {result ? (
            <div className="space-y-3">
              <div data-testid="seed-result" className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                <span className="text-sm font-medium">Done — {result.seeded.length} seeded, {result.skipped} already existed</span>
              </div>
              {result.seeded.length > 0 && (
                <ul className="space-y-0.5 text-xs text-gray-700">{result.seeded.map((t) => <li key={t}>✓ {t}</li>)}</ul>
              )}
            </div>
          ) : toSeed.length === 0 ? (
            <p className="text-sm text-gray-600">All {STOCK_CATALOG.length} stock drills already exist. Nothing to seed.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Will create <span className="font-semibold">{toSeed.length}</span> missing drill{toSeed.length !== 1 ? 's' : ''} with placeholder video URLs.
                {STOCK_CATALOG.length - toSeed.length > 0 && <> {STOCK_CATALOG.length - toSeed.length} already exist and will be skipped.</>}
              </p>
              <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-3">
                {STOCK_CATALOG.map((c) => {
                  const exists = existingTitles.has(c.title);
                  return (
                    <div key={c.title} className={`flex items-center gap-2 text-xs ${exists ? 'text-gray-400' : 'text-gray-700'}`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${exists ? 'bg-gray-300' : 'bg-blue-500'}`} />
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SHOT_TYPE_COLORS[c.shotType]}`}>{c.shotType}</span>
                      <span className="truncate">{c.title}</span>
                      {exists && <span className="ml-auto flex-shrink-0">exists</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button onClick={onClose} disabled={seeding} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && toSeed.length > 0 && (
            <button
              data-testid="seed-confirm-button"
              onClick={handleSeed}
              disabled={seeding}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center gap-2"
            >
              {seeding && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              {seeding ? 'Seeding…' : `Seed ${toSeed.length} drill${toSeed.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminClipLibrary() {
  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(true);
  const [shotFilter, setShotFilter] = useState<DrillShotType | 'all'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDrill, setEditDrill] = useState<Drill | null>(null);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Drill | null>(null);
  const [previewDrillId, setPreviewDrillId] = useState<string | null>(null);
  const [optimizingDrillIds, setOptimizingDrillIds] = useState<Set<string>>(new Set());
  const { showSuccess, showError } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const stock = await getStockDrills();
      setDrills(stock);
    } catch {
      showError('Failed to load drill library');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const filtered = shotFilter === 'all' ? drills : drills.filter((d) => d.shotType === shotFilter);

  async function triggerOptimize(drill: Drill) {
    setOptimizingDrillIds((prev) => new Set(prev).add(drill.id));
    try {
      const result = await preprocessDrillVideoForWeb(drill.id, drill.videoStoragePath, drill.videoUrl);
      setDrills((prev) =>
        prev.map((d) =>
          d.id === drill.id
            ? { ...d, webOptimizedVideoUrl: result.webOptimizedVideoUrl, webOptimizedStoragePath: result.webOptimizedStoragePath, durationSeconds: result.durationSeconds }
            : d
        )
      );
      showSuccess('Video optimized for web');
    } catch {
      showError('Failed to optimize video for web');
    } finally {
      setOptimizingDrillIds((prev) => {
        const next = new Set(prev);
        next.delete(drill.id);
        return next;
      });
    }
  }

  function handleSaved(saved: Drill, videoWasUploaded: boolean) {
    // Clear web-optimized state locally when the source video changed
    const drillForState = videoWasUploaded
      ? { ...saved, webOptimizedVideoUrl: undefined, webOptimizedStoragePath: undefined }
      : saved;
    setDrills((prev) => {
      const idx = prev.findIndex((d) => d.id === saved.id);
      const next = idx >= 0
        ? prev.map((d, i) => (i === idx ? drillForState : d))
        : [drillForState, ...prev];
      return next.sort((a, b) => a.shotType.localeCompare(b.shotType) || a.title.localeCompare(b.title));
    });
    setShowAddModal(false);
    setEditDrill(null);
    showSuccess(editDrill ? 'Drill updated' : 'Drill added');

    if (videoWasUploaded && !isPlaceholder(saved.videoUrl)) {
      triggerOptimize(saved);
    }
  }

  function handleSeeded(newDrills: Drill[]) {
    if (newDrills.length === 0) return;
    setDrills((prev) =>
      [...newDrills, ...prev].sort((a, b) => a.shotType.localeCompare(b.shotType) || a.title.localeCompare(b.title))
    );
  }

  async function handleDelete(drill: Drill) {
    try {
      await deleteDrill(drill.id);
      setDrills((prev) => prev.filter((d) => d.id !== drill.id));
      showSuccess('Drill deleted');
    } catch {
      showError('Failed to delete drill');
    } finally {
      setConfirmDelete(null);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Drill Library</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${drills.length} stock drill${drills.length !== 1 ? 's' : ''} — available to all coaches`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="seed-drills-button"
            onClick={() => setShowSeedModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Seed Stock Drills
          </button>
          <button
            data-testid="add-drill-button"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Drill
          </button>
        </div>
      </div>

      {/* Shot type filter */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        <button
          data-testid="shot-filter-all"
          onClick={() => setShotFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${shotFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
        >
          All
        </button>
        {SHOT_TYPES.map((t) => (
          <button
            key={t}
            data-testid={`shot-filter-${t}`}
            onClick={() => setShotFilter(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              shotFilter === t ? 'bg-gray-900 text-white border-gray-900' : `border-gray-200 hover:border-gray-400 ${SHOT_TYPE_COLORS[t]}`
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="empty-state" className="text-center py-16 text-gray-400">
          <svg className="w-10 h-10 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
          </svg>
          <p className="text-sm">{drills.length === 0 ? 'No drills yet. Add one or seed the stock catalog.' : 'No drills match the current filter.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((drill) => {
            const placeholder = isPlaceholder(drill.videoUrl);
            const isPreviewing = previewDrillId === drill.id;
            const isOptimizing = optimizingDrillIds.has(drill.id);
            const isWebOptimized = !!drill.webOptimizedVideoUrl;
            return (
              <div key={drill.id} data-testid="drill-item" className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-start gap-3 p-4">
                  {/* Shot type colour bar */}
                  <div className={`w-1.5 self-stretch rounded-full flex-shrink-0 ${SHOT_TYPE_COLORS[drill.shotType].split(' ')[0]}`} style={{ minHeight: 36 }} />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p data-testid="drill-title" className="font-semibold text-gray-900 text-sm">{drill.title}</p>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${SHOT_TYPE_COLORS[drill.shotType]}`}>
                        {drill.shotType}
                      </span>
                      {placeholder && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">No video</span>
                      )}
                      {isOptimizing && (
                        <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                          <span className="w-2 h-2 border border-blue-400 border-t-blue-600 rounded-full animate-spin inline-block" />
                          Optimizing…
                        </span>
                      )}
                      {!isOptimizing && isWebOptimized && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100">Web Optimized ✓</span>
                      )}
                    </div>
                    {drill.description && (
                      <p className="text-xs text-gray-500 line-clamp-2">{drill.description}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!placeholder && (
                      <button
                        onClick={() => setPreviewDrillId(isPreviewing ? null : drill.id)}
                        className={`px-2.5 py-1.5 text-xs border rounded-lg transition-colors ${isPreviewing ? 'border-blue-400 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                      >
                        {isPreviewing ? 'Hide' : 'Preview'}
                      </button>
                    )}
                    {!placeholder && !isOptimizing && !isWebOptimized && (
                      <button
                        onClick={() => triggerOptimize(drill)}
                        className="px-2.5 py-1.5 text-xs border border-indigo-200 rounded-lg hover:bg-indigo-50 text-indigo-600"
                      >
                        Optimize for Web
                      </button>
                    )}
                    <button
                      data-testid="edit-drill-button"
                      onClick={() => setEditDrill(drill)}
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                    >
                      Edit
                    </button>
                    <button
                      data-testid="delete-drill-button"
                      onClick={() => setConfirmDelete(drill)}
                      className="px-2.5 py-1.5 text-xs border border-red-200 rounded-lg hover:bg-red-50 text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {isPreviewing && (
                  <div className="border-t border-gray-100 bg-black">
                    <video src={drill.videoUrl} controls className="w-full max-h-60 object-contain" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {(showAddModal || editDrill) && (
        <DrillModal
          drill={editDrill ?? undefined}
          onClose={() => { setShowAddModal(false); setEditDrill(null); }}
          onSaved={handleSaved}
        />
      )}

      {showSeedModal && (
        <SeedModal
          existing={drills}
          onClose={() => setShowSeedModal(false)}
          onDone={handleSeeded}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Delete drill?</h3>
            <p className="text-sm text-gray-600 mb-5">"{confirmDelete.title}" will be permanently removed from the drill library.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="confirm-delete-button" onClick={() => handleDelete(confirmDelete)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
